// Sales > Dashboard — rep-scoped landing page with role-specific KPIs.
//
// Operator-locked 2026-05-20: 6 pulse tiles + 2 backlog tiles tailored
// to the Account Manager day-to-day workflow:
//   - Pipeline value ($)
//   - Presentations in flight (#)
//   - Comments waiting (#) — clients left comments via /p/:uuid
//   - Active orders (#) — my clients' orders moving
//   - Open AR ($)
//   - Revenue MTD ($)
//   Backlog: project requests from my clients · unrouted sign-ups
//
// Hits GET /sales/dashboard which defaults rep_id to the authed user.
// Service: apps/api/src/services/sales-dashboard.ts.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  DollarSign,
  Hand,
  MessageSquare,
  Plus,
  PresentationIcon as PresentationLucide,
  Receipt,
  TrendingUp,
  UserPlus,
  Inbox,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/PageHeader";
import { ThisWeekInHand, WorkInMotion } from "@/components/dashboard/WorkInMotion";
import { ProofsWaiting } from "@/components/dashboard/ProofsWaiting";
import { ProductionNeedsYou, WaitingOnClients } from "@/components/dashboard/RepFollowUps";
import {
  formatValue,
  type TileFormat,
} from "@/components/DashboardTiles";
import { NewProjectLauncherModal } from "@/components/projects/NewProjectLauncherModal";
import { MarkLostDialog, staleAlertText } from "@/components/opportunity/OpportunitySales";

type SalesPulseSlug =
  | "sales-pipeline-value"
  | "sales-presentations-in-flight"
  | "sales-comments-waiting"
  | "sales-active-orders"
  | "sales-open-ar"
  | "sales-revenue-mtd";

type SalesBacklogSlug =
  | "sales-project-requests-mine"
  | "sales-unrouted-project-requests"
  | "sales-unrouted-signups";

interface SalesDashboardResponse {
  pulse: Array<{
    slug: SalesPulseSlug;
    label: string;
    value: number;
    format: TileFormat;
    href: string;
    description: string;
  }>;
  backlog: Array<{
    slug: SalesBacklogSlug;
    label: string;
    value: number;
    format: TileFormat;
    href: string;
    description: string;
  }>;
  /** Opportunity stages (CEO 2026-10-15) — null before the DB step. */
  opportunities?: {
    pipelineValue: number;
    open: number;
    awaitingApproval: number;
    stale: number;
    wonThisMonth: number;
    lostThisMonth: number;
    staleList: Array<{
      projectId: string;
      name: string;
      clientName: string;
      stage: string;
      stageLabel: string;
      stageAgeDays: number;
      expectedValue: string | null;
    }>;
  } | null;
  fetchedAt: string;
}

const PULSE_ICON: Record<SalesPulseSlug, typeof DollarSign> = {
  "sales-pipeline-value": DollarSign,
  "sales-presentations-in-flight": PresentationLucide,
  "sales-comments-waiting": MessageSquare,
  "sales-active-orders": ClipboardList,
  "sales-open-ar": Receipt,
  "sales-revenue-mtd": TrendingUp,
};

// The dashboard's three numbers (Stephanie 2026-10-13). The server still sends
// the others; they're one tap away in Reports / Projects.
const KEY_NUMBERS: SalesPulseSlug[] = [
  "sales-pipeline-value",
  "sales-active-orders",
  "sales-revenue-mtd",
];

const BACKLOG_ICON: Record<SalesBacklogSlug, typeof Inbox> = {
  "sales-project-requests-mine": Inbox,
  "sales-unrouted-project-requests": Hand,
  "sales-unrouted-signups": UserPlus,
};

function firstName(name: string | null | undefined, email: string): string {
  if (name && name.trim().length > 0) {
    return name.split(/\s+/)[0] ?? "";
  }
  return email.split("@")[0] ?? "there";
}

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function SalesDashboardPage() {
  const { user } = useAuth();
  const greeting = timeOfDayGreeting();
  const first = user ? firstName(user.name, user.email) : "there";
  const navigate = useNavigate();

  // 2026-05-26 — operator-locked: "'New Project' should be an option
  // on the dashboard page for sales reps - so a rep can add one
  // immediately after logging in." Role-gated to admin / client_rep
  // (matches the POST /projects + POST /clients guards).
  const canCreate = (user?.roles ?? []).some(
    (r) => r === "admin" || r === "client_rep",
  );
  const [showNewProject, setShowNewProject] = useState(false);

  const isAdmin = (user?.roles ?? []).includes("admin");
  // Management sees every rep's opportunities; reps see their own.
  const [everyone, setEveryone] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["sales", "dashboard", everyone ? "all" : "mine"],
    queryFn: () => api<SalesDashboardResponse>(`/sales/dashboard${everyone ? "?all=1" : ""}`),
    refetchOnWindowFocus: true,
  });
  const opps = data?.opportunities ?? null;
  const [lostFor, setLostFor] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const snooze = useMutation({
    mutationFn: (projectId: string) => api(`/projects/${projectId}/snooze`, { method: "POST", body: "{}" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["sales"] }),
    onError: () => window.alert("Couldn't snooze that opportunity — try again."),
  });

  // Three numbers only: pipeline, active orders, revenue this month.
  // With opportunity stages on, pipeline value is the opportunities' expected
  // value (not the old budget sum) and lives in the Opportunities row.
  const keyNumbers = KEY_NUMBERS.filter((slug) => !(opps && slug === "sales-pipeline-value")).map((slug) => data?.pulse.find((t) => t.slug === slug)).filter(
    (t): t is NonNullable<typeof t> => t !== undefined,
  );
  const waiting = (data?.backlog ?? []).filter((b) => b.value > 0);

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Sales / Overview"
        title={`${greeting}, ${first}.`}
        intro={
          <>
            A clear next step for everything in motion.{" "}
            <Link to="/clients" className="text-ink underline underline-offset-4 hover:text-muted">All accounts</Link>
            {" · "}
            <Link to="/projects" className="text-ink underline underline-offset-4 hover:text-muted">All opportunities</Link>
          </>
        }
        action={
          canCreate ? (
            <Button type="button" onClick={() => setShowNewProject(true)}>
              <Plus className="h-4 w-4" aria-hidden /> New opportunity
            </Button>
          ) : undefined
        }
      />

      {/* Calmer layout (Stephanie 2026-10-13: "it looks too busy"): three
          numbers, "First things first" only when something is waiting, the
          five most recent projects, and the week's in-hands dates alongside. */}
      {isLoading ? (
        <div aria-busy="true" aria-label="Loading dashboard" className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-rule bg-rule sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white p-5">
              <div className="h-3 w-24 animate-pulse rounded bg-canvas" />
              <div className="mt-3 h-7 w-20 animate-pulse rounded bg-canvas" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-6 text-sm text-risk">
          We couldn&apos;t load your dashboard. Nothing has changed — refresh to try again.
        </div>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(240px,1fr)]">
          <div className="min-w-0 space-y-6">
            <section aria-label="Key numbers" className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-rule bg-rule sm:grid-cols-3">
              {keyNumbers.map((kpi) => {
                const Icon = PULSE_ICON[kpi.slug];
                return (
                  <Link key={kpi.slug} to={kpi.href} className="group flex flex-col bg-white p-5 transition-colors hover:bg-[#fafaf8]">
                    <span className="flex items-center justify-between gap-2 text-xs text-muted">
                      {kpi.label}
                      <Icon className="h-4 w-4 text-[#9a9a95] group-hover:text-ink" aria-hidden />
                    </span>
                    <span className="mt-1.5 text-[29px] font-medium leading-tight tracking-[-0.8px] text-ink tabular-nums">
                      {formatValue(kpi)}
                    </span>
                  </Link>
                );
              })}
            </section>

            {opps && (
              <section aria-label="Opportunities" className="rounded-md border border-rule bg-white">
                <div className="flex items-center justify-between gap-3 px-[22px] pt-4">
                  <h2 className="text-base font-semibold text-ink">Opportunities</h2>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setEveryone((v) => !v)}
                      className="text-sm text-muted underline underline-offset-4 hover:text-ink"
                    >
                      {everyone ? "Show mine" : "Show everyone's"}
                    </button>
                  )}
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-4 px-[22px] py-4 sm:grid-cols-3 xl:grid-cols-6">
                  {[
                    { label: "Pipeline value", value: opps.pipelineValue.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }), warn: false },
                    { label: "Open", value: String(opps.open), warn: false },
                    { label: "Awaiting approval", value: String(opps.awaitingApproval), warn: false },
                    { label: "Stale (21+ days)", value: String(opps.stale), warn: opps.stale > 0 },
                    { label: "Won this month", value: String(opps.wonThisMonth), warn: false },
                    { label: "Lost this month", value: String(opps.lostThisMonth), warn: false },
                  ].map((n) => (
                    <div key={n.label}>
                      <dt className="text-xs text-muted">{n.label}</dt>
                      <dd className={`mt-1 text-[24px] font-medium leading-tight tabular-nums ${n.warn ? "text-warn" : "text-ink"}`}>{n.value}</dd>
                    </div>
                  ))}
                </dl>
                {opps.staleList.length > 0 && (
                  <ul className="border-t border-rule px-[22px] py-2">
                    {opps.staleList.slice(0, 8).map((o) => (
                      <li key={o.projectId} className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e8e8e5] py-3 last:border-0">
                        <span className="min-w-0 text-[15px] text-ink">
                          <span className="font-medium">{o.name}</span>
                          <span className="text-muted"> · {o.clientName}</span>
                          <span className="block text-sm text-warn">{staleAlertText(o.stageLabel, o.stageAgeDays)}</span>
                        </span>
                        <span className="flex flex-wrap gap-2">
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/projects/${o.projectId}/quotes`}>Review opportunity</Link>
                          </Button>
                          <Button size="sm" variant="outline" disabled={snooze.isPending} onClick={() => snooze.mutate(o.projectId)}>
                            Snooze 7 days
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setLostFor(o.projectId)}>
                            Mark lost
                          </Button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {lostFor && (
                  <MarkLostDialog
                    projectId={lostFor}
                    onClose={() => setLostFor(null)}
                    onDone={() => {
                      setLostFor(null);
                      void queryClient.invalidateQueries({ queryKey: ["sales"] });
                    }}
                  />
                )}
              </section>
            )}

            {waiting.length > 0 && (
              <section className="rounded-md border border-rule bg-white px-[22px] py-4">
                <h2 className="mb-2 text-sm font-semibold text-ink">First things first.</h2>
                <ul>
                  {waiting.map((kpi) => {
                    const Icon = BACKLOG_ICON[kpi.slug];
                    return (
                      <li key={kpi.slug} className="border-b border-[#e8e8e5] last:border-0">
                        <Link to={kpi.href} className="-mx-2 flex items-center justify-between gap-3 rounded px-2 py-2.5 text-sm hover:bg-[#f1f1ee]">
                          <span className="flex min-w-0 items-center gap-2.5 text-ink">
                            <Icon className="h-4 w-4 shrink-0 text-muted" aria-hidden />
                            {kpi.label}
                          </span>
                          <span className="shrink-0 rounded bg-warn-bg px-2 py-0.5 text-[11px] font-medium text-warn tabular-nums">
                            {formatValue(kpi)} waiting
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            <ProductionNeedsYou />

            <WaitingOnClients />

            <ProofsWaiting />

            <WorkInMotion className="" />
          </div>
          <ThisWeekInHand />
        </div>
      )}

      {showNewProject && (
        <NewProjectLauncherModal
          onCancel={() => setShowNewProject(false)}
          onCreated={(result) => {
            setShowNewProject(false);
            navigate(`/projects/${result.projectId}`);
          }}
        />
      )}
    </div>
  );
}
